'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { ComponentData } from '@/lib/systemData'

interface SystemSidebarProps {
  component: ComponentData | null
  isOpen: boolean
  onClose: () => void
}

export default function SystemSidebar({ component, isOpen, onClose }: SystemSidebarProps) {
  if (!component) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-bg-secondary border-l border-border-subtle z-50 overflow-y-auto"
          >
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-light text-accent-primary mb-1">
                    {component.label}
                  </h3>
                  <p className="text-sm text-text-muted font-mono">
                    System Component
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-text-muted hover:text-text-primary transition-colors p-2"
                  aria-label="Close sidebar"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M15 5L5 15M5 5l10 10" />
                  </svg>
                </button>
              </div>

              {/* Description */}
              <div className="pt-4 border-t border-border-subtle">
                <h4 className="text-xs text-text-muted font-mono uppercase tracking-wider mb-2">
                  Description
                </h4>
                <p className="text-base text-text-secondary leading-relaxed">
                  {component.description}
                </p>
              </div>

              {/* Responsibility */}
              <div className="pt-4 border-t border-border-subtle">
                <h4 className="text-xs text-text-muted font-mono uppercase tracking-wider mb-2">
                  Responsibility
                </h4>
                <p className="text-base text-text-primary leading-relaxed">
                  {component.responsibility}
                </p>
              </div>

              {/* Failure Mode */}
              <div className="pt-4 border-t border-border-subtle">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <h4 className="text-xs text-red-400 font-mono uppercase tracking-wider">
                    Failure Mode
                  </h4>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                  <p className="text-sm text-red-200 leading-relaxed">
                    {component.failureMode}
                  </p>
                </div>
              </div>

              {/* Scaling Constraint */}
              <div className="pt-4 border-t border-border-subtle">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <h4 className="text-xs text-amber-400 font-mono uppercase tracking-wider">
                    Scaling Constraint
                  </h4>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4">
                  <p className="text-sm text-amber-200 leading-relaxed">
                    {component.scalingConstraint}
                  </p>
                </div>
              </div>

              {/* Technologies */}
              {component.technologies.length > 0 && (
                <div className="pt-4 border-t border-border-subtle">
                  <h4 className="text-xs text-text-muted font-mono uppercase tracking-wider mb-3">
                    Typical Technologies
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {component.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="text-xs text-accent-primary font-mono bg-accent-primary/10 border border-accent-primary/30 px-3 py-1.5 rounded-md"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Trade-off */}
              <div className="pt-4 border-t border-border-subtle">
                <h4 className="text-xs text-text-muted font-mono uppercase tracking-wider mb-2">
                  Key Trade-off
                </h4>
                <p className="text-sm text-text-secondary leading-relaxed italic">
                  {component.tradeoff}
                </p>
              </div>

              {/* Connected Components */}
              {component.connectedTo.length > 0 && (
                <div className="pt-4 border-t border-border-subtle">
                  <h4 className="text-xs text-text-muted font-mono uppercase tracking-wider mb-3">
                    Connected To
                  </h4>
                  <div className="space-y-2">
                    {component.connectedTo.map((id) => (
                      <div
                        key={id}
                        className="flex items-center gap-2 text-sm text-text-secondary"
                      >
                        <div className="w-1.5 h-1.5 bg-accent-primary rounded-full"></div>
                        <span className="font-mono">{id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
